import {
    useLazyGetProviderAvailabilityQuery,
    useScheduleAppointmentMutation,
    useLazyGetRecentAppointmentsQuery,
} from '@/rtkQuery/features/services/selfSchedule';

import type { ScheduleAppointmentRequest } from '@/rtkQuery/features/models/selfSchedule.model';
import { toastService } from '@/utils/toastService';

export const useSelfScheduleService = () => {
    const [
        triggerGetAvailability,
        {
            data: lazyData,
            error: lazyError,
            isLoading: lazyLoading,
            isSuccess: lazySuccess,
            isError: lazyFail,
            status: lazyStatus,
        },
    ] = useLazyGetProviderAvailabilityQuery();
    const [
        triggerSchedule,
        {
            data: scheduleData,
            isLoading: isScheduling,
            isError: isScheduleError,
            isSuccess: isScheduleSuccess,
            error: scheduleError,
            status: scheduleStatus,
        },
    ] = useScheduleAppointmentMutation();
    const [
        triggerGetRecentAppointments,
        {
            data: recentAppointmentsData,
            error: recentAppointmentsError,
            isLoading: recentAppointmentsLoading,
            isSuccess: recentAppointmentsSuccess,
            isError: recentAppointmentsFail,
            status: recentAppointmentsStatus,
        },
    ] = useLazyGetRecentAppointmentsQuery();

    const scheduleAppointment = async (payload: ScheduleAppointmentRequest) => {
        try {
            const response = await triggerSchedule(payload).unwrap();
            let message: any;
            if(response.Result === "Success") { 
                message = response.Message || 'Appointment scheduled successfully.';
                toastService.success(message);
            } else {
                message = response.Message || 'Failed to schedule appointment.';
                toastService.error(message);
            }
            return response;
        } catch (err: any) {
            const message =
                err?.data?.message || err?.error || err?.Message || 'Failed to schedule appointment.';
            toastService.error(message);
            throw err;
        }
    };

    const lazyErrorMessage =
        (lazyError as any)?.meta?.error || (lazyError as any)?.data?.message || 'Something went wrong';

    const scheduleErrorMessage =
        (scheduleError as any)?.meta?.error ||
        (scheduleError as any)?.data?.message ||
        (scheduleError as any)?.Message ||
        'Scheduling failed';

    const recentAppointmentsErrorMessage =
        (recentAppointmentsError as any)?.meta?.error ||
        (recentAppointmentsError as any)?.data?.message ||
        'Failed to fetch recent appointments';

    return { 
        fetchProviderAvailability: triggerGetAvailability,
        providerAvailabilityLazyData: lazyData,
        providerAvailabilityLazyError: lazyError,
        providerAvailabilityLazyIsLoading: lazyLoading,
        providerAvailabilityLazyIsSuccess: lazySuccess,
        providerAvailabilityLazyIsError: lazyFail,
        providerAvailabilityLazyStatus: lazyStatus,
        providerAvailabilityLazyErrorMessage: lazyErrorMessage,
       
        scheduleAppointment,
        scheduleAppointmentData: scheduleData,
        scheduleAppointmentIsLoading: isScheduling,
        scheduleAppointmentIsError: isScheduleError,
        scheduleAppointmentIsSuccess: isScheduleSuccess,
        scheduleAppointmentError: scheduleError,
        scheduleAppointmentErrorMessage: scheduleErrorMessage,
        scheduleAppointmentStatus: scheduleStatus,

        fetchRecentAppointments: triggerGetRecentAppointments,
        recentAppointmentsData: recentAppointmentsData,
        recentAppointmentsError: recentAppointmentsError,
        recentAppointmentsIsLoading: recentAppointmentsLoading,
        recentAppointmentsIsSuccess: recentAppointmentsSuccess,
        recentAppointmentsIsError: recentAppointmentsFail,
        recentAppointmentsStatus: recentAppointmentsStatus,
        recentAppointmentsErrorMessage: recentAppointmentsErrorMessage,
    };
};

